<template>
  <Content>
    <form @submit.prevent="submitForm">
      <div class="container-fluid py-4">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3"
        >
          <div>
            <h3 class="page-title mb-1">{{ currentLang.title }}</h3>
            <p class="page-subtitle mb-0">{{ currentLang.detail }}</p>
          </div>

          <button
            type="button"
            @click="$router.push('/orders')"
            class="btn btn-light back-btn"
          >
            {{ currentLang.back }}
          </button>
        </div>

        <ErrorAlert v-if="errorMessage" :message="errorMessage" />

        <div class="row g-4">
          <!-- Customer -->
          <div class="col-12 col-lg-6">
            <label class="custom-label">
              {{ currentLang.customer }}
              <span class="required">*</span>
            </label>

            <input
              class="form-control custom-input"
              list="list-customers"
              v-model="customerName"
              @input="handleInput('customer', customerName)"
              :placeholder="currentLang.placeholderCustomer"
              :disabled="isEdit"
              required
            />

            <datalist id="list-customers">
              <option
                v-for="customer in customers"
                :key="customer.id"
                :value="getCustomerLabel(customer)"
              />
            </datalist>
          </div>

          <!-- Shipping Method -->
          <div class="col-12 col-md-6">
            <label class="custom-label">
              {{ currentLang.shippingMethod }}
              <span class="required">*</span>
            </label>

            <input
              class="form-control custom-input"
              list="list-shipping-methods"
              :value="shippingMethodName"
              @input="handleInput('shipping_method', $event.target.value)"
              @change="syncSelection('shipping_method')"
              :placeholder="currentLang.placeholderShippingMethod"
              required
            />

            <datalist id="list-shipping-methods">
              <option
                v-for="method in shippingMethods"
                :key="method.id"
                :value="method.name_fa"
              />
            </datalist>
          </div>

          <!-- Payment Method -->
          <div class="col-12 col-md-6">
            <label class="custom-label">
              {{ currentLang.paymentMethod }}
              <span class="required">*</span>
            </label>

            <input
              class="form-control custom-input"
              list="list-payment-methods"
              :value="paymentMethodName"
              @input="handleInput('payment_method', $event.target.value)"
              @change="syncSelection('payment_method')"
              :placeholder="currentLang.placeholderPaymentMethod"
              required
            />

            <datalist id="list-payment-methods">
              <option
                v-for="method in paymentMethods"
                :key="method.id"
                :value="method.name_fa"
              />
            </datalist>
          </div>

          <!-- Address -->
          <div class="col-12">
            <label class="custom-label">
              {{ currentLang.address }}
              <span class="required">*</span>
            </label>

            <select
              class="form-select custom-input"
              v-model="form.address_id"
              :disabled="!validCustomerAddresses.length"
              required
            >
              <!-- <option disabled value="">{{ currentLang.selectAddress }}</option> -->
              <option
                v-for="address in validCustomerAddresses"
                :key="address.id"
                :value="address.id"
              >
                {{ formatAddress(address) }}
              </option>
            </select>
          </div>

          <!-- Address Details Preview -->
          <div class="col-12" v-if="selectedAddress">
            <div class="card border-0 shadow-sm address-card">
              <div class="card-body">
                <h6 class="mb-3">{{ currentLang.addressDetails }}</h6>
                <div class="row g-3">
                  <div class="col-12 col-md-6">
                    <strong>{{ currentLang.addressTitle }}:</strong>
                    {{ selectedAddress.title || "-" }}
                  </div>
                  <div class="col-12 col-md-6">
                    <strong>{{ currentLang.addressPhone }}:</strong>
                    {{ selectedAddress.phone || "-" }}
                  </div>
                  <div class="col-12 col-md-6">
                    <strong>{{ currentLang.postalCode }}:</strong>
                    {{ selectedAddress.postal_code || "-" }}
                  </div>
                  <div class="col-12">
                    <strong>{{ currentLang.fullAddress }}:</strong>
                    {{ selectedAddress.address || "-" }}
                  </div>
                  <div class="col-12" v-if="selectedAddress.details">
                    <strong>{{ currentLang.addressExtraDetails }}:</strong>
                    {{ selectedAddress.details }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Products / Invoice Items -->
          <div class="col-12">
            <div class="card invoice-main-card border-0">
              <div class="card-body p-4">
                <div
                  class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2"
                >
                  <div>
                    <h5 class="mb-1 fw-bold text-dark">
                      {{ currentLang.items }}
                    </h5>
                    <small class="text-muted"
                      >لیست محصولات فاکتور را مدیریت کنید</small
                    >
                  </div>

                  <button
                    type="button"
                    class="btn back-btn btn-sm px-3"
                    @click="addItem"
                  >
                    + {{ currentLang.addItem }}
                  </button>
                </div>

                <div class="invoice-items">
                  <div
                    v-for="(item, index) in form.items"
                    :key="item.product_id || index"
                    class="invoice-item-row"
                  >
                    <div class="row g-2 align-items-end">
                      <!-- Product -->
                      <div class="col-12 col-lg-5">
                        <label class="form-label compact-label">
                          {{ currentLang.product }}
                          <span class="required">*</span>
                        </label>

                        <input
                          class="form-control compact-input"
                          :list="`list-products-${index}`"
                          :value="item.product_name"
                          @input="
                            handleProductInput(index, $event.target.value)
                          "
                          @change="syncProduct(index)"
                          :placeholder="currentLang.placeholderProduct"
                          required
                        />

                        <datalist :id="`list-products-${index}`">
                          <option
                            v-for="product in item.products"
                            :key="product.id"
                            :value="product.name_fa"
                          />
                        </datalist>
                      </div>

                      <!-- Qty -->
                      <div class="col-6 col-lg-1">
                        <label class="form-label compact-label">
                          {{ currentLang.quantity }}
                          <span class="required">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          class="form-control compact-input"
                          v-model.number="item.quantity"
                          @input="handleInvoiceChange"
                          :disabled="!item.product_id"
                          required
                        />
                      </div>

                      <!-- Discount -->
                      <div class="col-6 col-lg-1">
                        <label class="form-label compact-label">{{
                          currentLang.itemDiscount
                        }}</label>
                        <input
                          type="number"
                          min="0"
                          class="form-control compact-input"
                          v-model.number="item.discount"
                          :disabled="!item.product_id"
                          @input="handleInvoiceChange"
                        />
                      </div>

                      <!-- Unit Price -->
                      <div class="col-6 col-lg-2">
                        <label class="form-label compact-label">{{
                          currentLang.price
                        }}</label>
                        <input
                          type="text"
                          class="form-control compact-input input-disabled"
                          :value="formatNumber(item.price)"
                          disabled
                        />
                      </div>

                      <!-- Total -->
                      <div class="col-6 col-lg-2">
                        <label class="form-label compact-label">{{
                          currentLang.total
                        }}</label>
                        <input
                          type="text"
                          class="form-control compact-input input-disabled fw-bold total-input"
                          :value="formatNumber(item.total)"
                          disabled
                        />
                      </div>

                      <!-- Delete -->
                      <div class="col-12 col-lg-1">
                        <label
                          class="form-label compact-label d-none d-lg-block"
                          >&nbsp;</label
                        >
                        <button
                          type="button"
                          class="btn btn-delete-compact w-100"
                          @click="removeItem(index)"
                          :disabled="form.items.length === 1"
                          title="حذف"
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    <!-- meta row (جمع‌وجور) -->
                    <div class="invoice-meta-line">
                      <span
                        ><small>{{ currentLang.subtotal }}:</small>
                        <b>{{ formatNumber(item.subtotal) }}</b></span
                      >
                      <span
                        ><small>{{ currentLang.tax }}:</small>
                        <b>{{ formatNumber(item.tax) }}</b></span
                      >
                      <span
                        ><small>{{ currentLang.discount }}:</small>
                        <b>{{ formatNumber(item.discount_server) }}</b></span
                      >
                      <span
                        ><small>{{ currentLang.discountPaymentMethod }}:</small>
                        <b>{{
                          formatNumber(item.discount_payment_method)
                        }}</b></span
                      >
                      <span class="meta-highlight"
                        ><small>{{ currentLang.totalDiscount }}:</small>
                        <b>{{ formatNumber(item.total_discount) }}</b></span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Invoice Summary -->
          <div class="col-12">
            <div class="card border-0 shadow-sm invoice-summary-card">
              <div class="card-body">
                <div
                  class="d-flex justify-content-between align-items-center mb-4"
                >
                  <h5 class="mb-0">{{ currentLang.summary }}</h5>
                  <span v-if="checkingInvoice" class="invoice-loading">
                    {{ currentLang.calculating }}
                  </span>
                </div>

                <div class="invoice-summary">
                  <div class="summary-row">
                    <span>{{ currentLang.subtotal }}</span>
                    <strong>{{ formatNumber(form.subtotal) }}</strong>
                  </div>

                  <div class="summary-row">
                    <span>{{ currentLang.tax }}</span>
                    <strong>{{ formatNumber(form.tax) }}</strong>
                  </div>

                  <div class="summary-row">
                    <span>{{ currentLang.shippingCost }}</span>
                    <strong>{{ formatNumber(form.shipping_cost) }}</strong>
                  </div>

                  <div class="summary-row text-danger">
                    <span>{{ currentLang.discount }}</span>
                    <strong>{{ formatNumber(form.discount) }}</strong>
                  </div>

                  <div class="summary-row text-danger">
                    <span>{{ currentLang.discountPaymentMethod }}</span>
                    <strong>{{
                      formatNumber(form.discount_payment_method)
                    }}</strong>
                  </div>

                  <div class="summary-row text-danger">
                    <span>{{ currentLang.totalDiscount }}</span>
                    <strong>{{ formatNumber(form.total_discount) }}</strong>
                  </div>

                  <hr />

                  <div class="summary-row grand-total">
                    <span>{{ currentLang.totalPrice }}</span>
                    <strong>{{ formatNumber(form.total_price) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit -->
          <div class="d-flex justify-content-end mt-4">
            <button class="btn save-btn" :disabled="loading || checkingInvoice">
              {{ loading ? currentLang.saving : currentLang.save }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </Content>
</template>

<script setup>
import { alertStore } from "@/stores/alertStore";
import Content from "@/components/AdminContent.vue";
import { lang } from "@/plugins/lang";
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import api from "@/plugins/axios";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import { showLoading, hideLoading } from "@/stores/loading";

import enText from "@/locales/en/orderForm";
import faText from "@/locales/fa/orderForm";

const translations = {
  en: enText,
  fa: faText,
};

const currentLang = computed(() => {
  const current = String(lang.value).toLowerCase();
  if (current.startsWith("fa")) return translations.fa;
  if (current.startsWith("en")) return translations.en;
  return translations.fa;
});
let customerTimer = null;
const route = useRoute();
const router = useRouter();

const errorMessage = ref("");
const loading = ref(false);
const checkingInvoice = ref(false);

const isEdit = computed(() => !!route.params.id);

const customers = ref([]);
const paymentMethods = ref([]);
const shippingMethods = ref([]);
const customerAddresses = ref([]);

const customerName = ref("");
const paymentMethodName = ref("");
const shippingMethodName = ref("");

let timers = {
  customer: null,
  payment_method: null,
  shipping_method: null,
};

let orderCheckTimer = null;

const createEmptyItem = () => ({
  uid: "",
  product_id: "",
  product_name: "",
  quantity: 1,
  discount: 0,
  price: 0,
  subtotal: 0,
  tax: 0,
  total: 0,
  discount_server: 0,
  discount_payment_method: 0,
  total_discount: 0,
  products: [],
  timer: null,
});

const form = ref({
  user_id: "",
  status: "registered",
  payment_method_id: "",
  shipping_method_id: "",
  address_id: "",
  subtotal: 0,
  discount_payment_method: 0,
  discount: 0,
  total_discount: 0,
  shipping_cost: 0,
  tax: 0,
  total_price: 0,
  items: [createEmptyItem()],
});

const validCustomerAddresses = computed(() => {
  return customerAddresses.value.filter((address) => {
    return (
      address.title ||
      address.phone ||
      address.postal_code ||
      address.address ||
      address.details
    );
  });
});

const selectedAddress = computed(() => {
  return (
    customerAddresses.value.find(
      (address) => Number(address.id) === Number(form.value.address_id)
    ) || null
  );
});

const canCheckOrder = computed(() => {
  return (
    !!form.value.user_id &&
    !!form.value.shipping_method_id &&
    !!form.value.payment_method_id &&
    form.value.items.length > 0 &&
    form.value.items.every(
      (item) => item.product_id && Number(item.quantity) > 0
    )
  );
});

const getCustomerLabel = (customer) => {
  const firstName = customer.first_name || "";
  const lastName = customer.last_name || "";
  const mobile = customer.mobile || "";

  return `${firstName} ${lastName} - ${mobile}`.trim();
};

const formatAddress = (address) => {
  const parts = [address.title, address.phone, address.address].filter(Boolean);

  return parts.length ? parts.join(" - ") : currentLang.value.emptyAddress;
};

const formatNumber = (value) => {
  return Number(value || 0).toLocaleString("en-US");
};

const searchCustomers = async (query = "") => {
  try {
    const response = await api.get(`/admin/client/list?name=${query}`);
    customers.value = response.data.data || [];
  } catch (error) {
    console.log(error);
  }
};

const searchPaymentMethods = async (query = "") => {
  try {
    const response = await api.get(`/admin/payment-methods/list?name=${query}`);
    paymentMethods.value = response.data.data || [];
  } catch (error) {
    console.log(error);
  }
};

const searchShippingMethods = async (query = "") => {
  try {
    const response = await api.get(
      `/admin/shipping-methods/list?name=${query}`
    );
    shippingMethods.value = response.data.data || [];
  } catch (error) {
    console.log(error);
  }
};

const searchProducts = async (index, query = "") => {
  try {
    const response = await api.get(`/admin/products/list?name=${query}`);
    form.value.items[index].products = response.data.data || [];
  } catch (error) {
    console.log(error);
  }
};

const handleInput = (type, value) => {
  if (type === "customer") {
    customerName.value = value;

    const selected = customers.value.find(
      (customer) => getCustomerLabel(customer) === value
    );

    // اگر از لیست انتخاب شده
    if (selected) {
      form.value.user_id = selected.id || "";
      customerAddresses.value = selected.addresses || [];
      return;
    }

    // اگر فقط تایپ شده، هنوز انتخاب نشده => سرچ کن
    clearTimeout(customerTimer);
    customerTimer = setTimeout(async () => {
      await getCustomers(value);
    }, 300);
  }

  if (type === "payment_method") {
    paymentMethodName.value = value;
    form.value.payment_method_id = "";
    handleInvoiceChange();
  }

  if (type === "shipping_method") {
    shippingMethodName.value = value;
    form.value.shipping_method_id = "";
    handleInvoiceChange();
  }

  clearTimeout(timers[type]);
  timers[type] = setTimeout(() => {
    if (type === "customer") searchCustomers(value);
    if (type === "payment_method") searchPaymentMethods(value);
    if (type === "shipping_method") searchShippingMethods(value);
  }, 400);
};

const handleProductInput = (index, value) => {
  form.value.items[index].product_name = value;
  form.value.items[index].product_id = "";
  form.value.items[index].price = 0;
  form.value.items[index].subtotal = 0;
  form.value.items[index].tax = 0;
  form.value.items[index].total = 0;
  form.value.items[index].discount_server = Number(
    form.value.items[index].discount || 0
  );
  form.value.items[index].discount_payment_method = 0;
  form.value.items[index].total_discount = Number(
    form.value.items[index].discount || 0
  );

  handleInvoiceChange();

  clearTimeout(form.value.items[index].timer);
  form.value.items[index].timer = setTimeout(() => {
    searchProducts(index, value);
  }, 400);
};

const syncSelection = async (type) => {
  if (type === "customer") {
    const selectedCustomer = customers.value.find(
      (customer) => getCustomerLabel(customer) === customerName.value
    );

    if (selectedCustomer) {
      form.value.user_id = selectedCustomer.id;
      customerAddresses.value = selectedCustomer.addresses || [];
      form.value.address_id = "";
      handleInvoiceChange();
    }
  }

  if (type === "payment_method") {
    const selectedPaymentMethod = paymentMethods.value.find(
      (method) => method.name_fa === paymentMethodName.value
    );

    if (selectedPaymentMethod) {
      form.value.payment_method_id = selectedPaymentMethod.id;
      handleInvoiceChange();
    }
  }

  if (type === "shipping_method") {
    const selectedShippingMethod = shippingMethods.value.find(
      (method) => method.name_fa === shippingMethodName.value
    );

    if (selectedShippingMethod) {
      form.value.shipping_method_id = selectedShippingMethod.id;
      handleInvoiceChange();
    }
  }
};

const syncProduct = (index) => {
  const item = form.value.items[index];
  const selectedProduct = item.products.find(
    (product) => product.name_fa === item.product_name
  );

  if (selectedProduct) {
    item.product_id = selectedProduct.id;
    handleInvoiceChange();
  }
};

const buildOrderCheckPayload = () => {
  return {
    user_id: form.value.user_id,
    order_id: route.params.id,
    shipping_method_id: form.value.shipping_method_id,
    payment_method_id: form.value.payment_method_id,
    items: form.value.items
      .filter((item) => item.product_id && Number(item.quantity) > 0)
      .map((item) => ({
        product_id: item.product_id,
        quantity: Number(item.quantity || 0),
        discount: Number(item.discount || 0),
      })),
  };
};

const orderCheck = async () => {
  if (!canCheckOrder.value) {
    return;
  }

  try {
    checkingInvoice.value = true;
    errorMessage.value = "";
    const payload = buildOrderCheckPayload();
    const response = await api.post("/admin/orders/check", payload);
    const data = response.data.data || response.data;

    form.value.subtotal = Number(data.subtotal || 0);
    form.value.tax = Number(data.tax || 0);
    form.value.shipping_cost = Number(data.shipping_cost || 0);
    form.value.discount = Number(data.discount || 0);
    form.value.discount_payment_method = Number(
      data.discount_payment_method || 0
    );
    form.value.total_discount = Number(data.total_discount || 0);
    form.value.total_price = Number(data.total_price || 0);

    const checkedItems = Array.isArray(data.items) ? data.items : [];

    const serverItemsMap = new Map(
      checkedItems.map((serverItem) => [
        Number(serverItem.product_id),
        serverItem,
      ])
    );

    form.value.items = form.value.items.map((item) => {
      const serverItem = serverItemsMap.get(Number(item.product_id));

      if (!serverItem) {
        return {
          ...item,
          price: 0,
          subtotal: 0,
          tax: 0,
          total: 0,
          discount_server: Number(item.discount || 0),
          discount_payment_method: 0,
          total_discount: Number(item.discount || 0),
        };
      }

      return {
        ...item,
        price: Number(serverItem.price || 0),
        subtotal: Number(serverItem.subtotal || 0),
        tax: Number(serverItem.tax || 0),
        total: Number(serverItem.total || 0),
        discount_server: Number(serverItem.discount || 0),
        discount_payment_method: Number(
          serverItem.discount_payment_method || 0
        ),
        total_discount: Number(serverItem.total_discount || 0),
      };
    });
  } catch (error) {
    errorMessage.value =
      Object.values(error.response?.data?.errors || {})
        .flat()
        .join("\n") ||
      error.response?.data?.message ||
      "خطایی رخ داده است";
  } finally {
    checkingInvoice.value = false;
  }
};

const handleInvoiceChange = () => {
  clearTimeout(orderCheckTimer);
  orderCheckTimer = setTimeout(() => {
    orderCheck();
  }, 500);
};

const addItem = () => {
  form.value.items.push(createEmptyItem());
};

const removeItem = (index) => {
  if (form.value.items.length === 1) return;
  form.value.items.splice(index, 1);
  handleInvoiceChange();
};

const buildPayload = () => {
  return {
    user_id: form.value.user_id,
    status: form.value.status,
    payment_method_id: form.value.payment_method_id,
    shipping_method_id: form.value.shipping_method_id,
    address_id: form.value.address_id,
    subtotal: Number(form.value.subtotal || 0),
    discount_payment_method: Number(form.value.discount_payment_method || 0),
    discount: Number(form.value.discount || 0),
    total_discount: Number(form.value.total_discount || 0),
    shipping_cost: Number(form.value.shipping_cost || 0),
    tax: Number(form.value.tax || 0),
    total_price: Number(form.value.total_price || 0),
    items: form.value.items.map((item) => ({
      product_id: item.product_id,
      quantity: Number(item.quantity || 0),
      discount: Number(item.discount || 0),
      price: Number(item.price || 0),
      subtotal: Number(item.subtotal || 0),
    })),
  };
};

const submitForm = async () => {
  try {
    loading.value = true;
    showLoading();
    errorMessage.value = "";

    await orderCheck();

    const payload = buildPayload();

    const response = isEdit.value
      ? await api.patch(`/admin/orders/${route.params.id}`, payload)
      : await api.post(`/admin/orders`, payload);

    if (response.status === 200 || response.status === 201) {
      alertStore.trigger(
        "success",
        currentLang.value.success,
        response?.data?.message || ""
      );
      router.push("/orders");
    } else {
      errorMessage.value =
        response?.data?.message || currentLang.value.networkError;
    }
  } catch (error) {
    errorMessage.value =
      error.response?.data?.message || currentLang.value.error;
  } finally {
    loading.value = false;
    hideLoading();
  }
};

const getOrder = async () => {
  try {
    loading.value = true;
    showLoading();

    const response = await api.get(`/admin/orders/${route.params.id}`);
    const order = response.data.data;

    form.value.user_id = order.user?.id || "";
    form.value.status = order.status || "registered";
    form.value.payment_method_id = order.payment_method?.id || "";
    form.value.shipping_method_id = order.shipping_method?.id || "";
    form.value.address_id = order.address?.id || "";
    form.value.subtotal = Number(order.subtotal || 0);
    form.value.discount_payment_method = Number(
      order.discount_payment_method || 0
    );
    form.value.discount = Number(order.discount || 0);
    form.value.total_discount = Number(order.total_discount || 0);
    form.value.shipping_cost = Number(order.shipping_cost || 0);
    form.value.tax = Number(order.tax || 0);
    form.value.total_price = Number(order.total_price || 0);

    customerName.value = `${order.user?.first_name || ""} ${
      order.user?.last_name || ""
    } - ${order.user?.mobile || ""}`.trim();
    paymentMethodName.value = order.payment_method?.name_fa || "";
    shippingMethodName.value = order.shipping_method?.name_fa || "";

    customerAddresses.value = order.user?.addresses || [];

    form.value.items = order.items?.map((item) => ({
      uid: item.product_id,
      product_id: item.product?.id || item.product_id || "",
      product_name: item.product?.name_fa || "",
      quantity: Number(item.quantity || 1),
      discount: Number(item.discount || 0),
      price: Number(item.price || 0),
      subtotal: Number(item.subtotal || 0),
      tax: Number(item.tax || 0),
      total: Number(item.total || 0),
      discount_server: Number(item.discount || 0),
      discount_payment_method: Number(item.discount_payment_method || 0),
      total_discount: Number(item.total_discount || 0),
      products: item.product ? [item.product] : [],
      timer: null,
    })) || [createEmptyItem()];

    if (!form.value.items.length) {
      form.value.items = [createEmptyItem()];
    }

    handleInvoiceChange();
  } catch (error) {
    errorMessage.value =
      error.response?.data?.message || currentLang.value.error;
  } finally {
    loading.value = false;
    hideLoading();

  }
};

onMounted(async () => {
  await Promise.all([searchPaymentMethods(""), searchShippingMethods("")]);

  if (isEdit.value) {
    await getOrder();
  } else {
    await searchCustomers("");
    await searchProducts(0, "");
  }
});
</script>

<style scoped>
.page-title {
  font-weight: 700;
  color: #1f2937;
}

.page-subtitle {
  color: #6b7280;
  font-size: 0.95rem;
}

.custom-input {
  min-height: 48px;
  border-radius: 12px;
  border: 1px solid #dbe1ea;
  box-shadow: none !important;
  transition: 0.2s ease;
}

.custom-input:focus {
  border-color: #86b7fe;
}

.order-card,
.address-card,
.invoice-summary-card {
  border-radius: 18px;
}

.save-btn {
  min-width: 160px;
  height: 48px;
  border-radius: 12px;
  background: #0d6efd;
  color: #fff;
  border: none;
  font-weight: 600;
}

.save-btn:hover {
  background: #0b5ed7;
  color: #fff;
}

.back-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  background: #ffffff;
  color: #334155;
  font-weight: 600;
  transition: 0.2s ease;

  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
}

.invoice-item {
  background: #fcfcfd;
  border: 1px solid #e9edf3;
}

.invoice-item-meta {
  padding-top: 8px;
  border-top: 1px dashed #e5e7eb;
  margin-top: 12px;
}

.invoice-summary {
  max-width: 520px;
  margin-inline-start: auto;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  font-size: 0.96rem;
  border-bottom: 1px solid #f1f3f5;
}

.summary-row:last-child {
  border-bottom: none;
}

.grand-total {
  font-size: 1.08rem;
  font-weight: 700;
  color: #111827;
}

.invoice-loading {
  font-size: 0.9rem;
  color: #6b7280;
}

.invoice-main-card {
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.invoice-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.1);
  border-color: #dbe7ff;
}

.custom-label {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 8px;
}

.modern-input:focus {
  border-color: #4f8cff;
  box-shadow: 0 0 0 4px rgba(79, 140, 255, 0.12);
}

.modern-input:disabled,
.input-disabled {
  background: #f8fafc !important;
  color: #475467;
  opacity: 1;
}

.btn-add-item {
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  border: none;
  height: 40px;
  font-weight: 600;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
  transition: all 0.25s ease;
}

.btn-add-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(37, 99, 235, 0.3);
  color: #fff;
}

.btn-delete-item {
  height: 44px;
  border-radius: 12px;
  border: 1px solid #fecaca;
  background: #fff5f5;
  color: #dc2626;
  font-size: 22px;
  font-weight: bold;
  transition: all 0.2s ease;
}

.btn-delete-item:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #b91c1c;
}

.meta-box {
  background: #f8fafc;
  border: 1px solid #edf2f7;
  border-radius: 14px;
  padding: 12px 14px;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.meta-box small {
  color: #667085;
  font-size: 12px;
  margin-bottom: 6px;
}

.meta-box strong {
  color: #101828;
  font-size: 15px;
  font-weight: 700;
}

.meta-box.highlight {
  background: linear-gradient(135deg, #eff6ff, #eef4ff);
  border-color: #bfdbfe;
}

.invoice-item-row {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  padding: 14px 14px 10px;
  margin-bottom: 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
}

.compact-label {
  font-size: 12px;
  font-weight: 700;
  color: #344054;
  margin-bottom: 6px;
  white-space: nowrap;
}

.required {
  color: #ef4444;
}

.compact-input {
  height: 40px;
  border-radius: 12px;
  border: 1px solid #d0d7e2;
  font-size: 14px;
  padding: 8px 10px;
}

.compact-input:focus {
  border-color: #4f8cff;
  box-shadow: 0 0 0 4px rgba(79, 140, 255, 0.12);
}

.input-disabled {
  background: #f8fafc !important;
  opacity: 1;
  color: #475467;
}

.total-input {
  color: #0f766e;
}

.btn-delete-compact {
  height: 40px;
  border-radius: 12px;
  border: 1px solid #fecaca;
  background: #fff5f5;
  color: #dc2626;
  font-size: 22px;
  font-weight: 900;
  line-height: 1;
}

.invoice-meta-line {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e7eb;

  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;

  font-size: 12px;
  color: #667085;
}

.invoice-meta-line b {
  color: #101828;
  font-weight: 800;
}

.invoice-meta-line span {
  background: #f8fafc;
  border: 1px solid #edf2f7;
  padding: 6px 10px;
  border-radius: 999px;
}

.invoice-meta-line .meta-highlight {
  background: #eff6ff;
  border-color: #bfdbfe;
}

@media (max-width: 992px) {
  .invoice-meta-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .invoice-main-card .card-body {
    padding: 16px !important;
  }

  .invoice-item-card {
    padding: 16px;
    border-radius: 14px;
  }

  .invoice-meta-grid {
    grid-template-columns: 1fr;
  }

  .modern-input,
  .btn-delete-item {
    height: 42px;
  }
}

@media (max-width: 767.98px) {
  .page-title {
    font-size: 1.25rem;
  }

  .page-subtitle {
    font-size: 0.85rem;
  }

  .save-btn {
    width: 100%;
  }
}
</style>
